import { useState, useEffect } from 'react';
import { db, storage } from '../lib/firebase';
import { collection, getDocs, query, orderBy, deleteDoc, doc, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

interface Player {
    id: string;
    firstName: string;
    lastName: string;
    season?: string;
}

interface DocumentItem {
    id: string;
    name: string;
    description?: string;
    url: string;
    category: 'permission-slip' | 'form' | 'policy' | 'other';
    playerId?: string;
    playerName?: string;
    uploadedAt: Date;
    uploadedBy: string;
}

const CATEGORIES = [
    { value: 'permission-slip', label: '📝 Permission Slip', color: 'bg-green-500/20 text-green-400' },
    { value: 'form', label: '📋 Form', color: 'bg-blue-500/20 text-blue-400' },
    { value: 'policy', label: '📖 Policy', color: 'bg-yellow-500/20 text-yellow-400' },
    { value: 'other', label: '📁 Other', color: 'bg-secondary-700 text-secondary-300' },
];

export default function AdminDocumentsPage() {
    const [documents, setDocuments] = useState<DocumentItem[]>([]);
    const [players, setPlayers] = useState<Player[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');

    const [uploadForm, setUploadForm] = useState({
        name: '',
        description: '',
        category: 'permission-slip' as DocumentItem['category'],
        playerId: '',
        file: null as File | null,
    });

    // Load documents and players
    useEffect(() => {
        loadData();
    }, []);

    async function loadData() {
        try {
            // Load documents
            const docsQuery = query(collection(db, 'documents'), orderBy('uploadedAt', 'desc'));
            const docsSnap = await getDocs(docsQuery);
            const docs = docsSnap.docs.map(d => ({
                id: d.id,
                ...d.data(),
                uploadedAt: d.data().uploadedAt?.toDate() || new Date(),
            })) as DocumentItem[];
            setDocuments(docs);

            // Load players for association picker
            const playersSnap = await getDocs(collection(db, 'playerRoster'));
            const playersData = playersSnap.docs
                .map(d => ({ id: d.id, ...d.data() } as Player))
                .sort((a, b) => a.lastName.localeCompare(b.lastName));
            setPlayers(playersData);
        } catch (err) {
            console.error('Error loading data:', err);
        } finally {
            setLoading(false);
        }
    }

    async function handleUpload(e: React.FormEvent) {
        e.preventDefault();
        if (!uploadForm.file || !uploadForm.name) return;

        setUploading(true);
        try {
            // Find player name if selected
            const selectedPlayer = players.find(p => p.id === uploadForm.playerId);
            const playerName = selectedPlayer
                ? `${selectedPlayer.firstName} ${selectedPlayer.lastName}`
                : null;

            // Upload file to storage
            const fileName = `documents/${Date.now()}_${uploadForm.file.name}`;
            const storageRef = ref(storage, fileName);
            await uploadBytes(storageRef, uploadForm.file);
            const url = await getDownloadURL(storageRef);

            // Add document record to Firestore
            await addDoc(collection(db, 'documents'), {
                name: uploadForm.name,
                description: uploadForm.description,
                category: uploadForm.category,
                playerId: uploadForm.playerId || null,
                playerName: playerName,
                url,
                storagePath: fileName,
                uploadedAt: Timestamp.now(),
                uploadedBy: 'admin',
            });

            setShowUploadModal(false);
            setUploadForm({ name: '', description: '', category: 'permission-slip', playerId: '', file: null });
            loadData();
        } catch (err) {
            console.error('Error uploading document:', err);
            alert('Failed to upload document');
        } finally {
            setUploading(false);
        }
    }

    async function handleDelete(docItem: DocumentItem) {
        if (!confirm(`Delete "${docItem.name}"? This cannot be undone.`)) return;

        try {
            await deleteDoc(doc(db, 'documents', docItem.id));
            loadData();
        } catch (err) {
            console.error('Error deleting document:', err);
            alert('Failed to delete document');
        }
    }

    const getCategoryInfo = (category: string) => {
        return CATEGORIES.find(c => c.value === category) || CATEGORIES[3];
    };

    // Filter documents
    const filteredDocuments = documents.filter(doc => {
        // Search filter
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = !searchQuery ||
            doc.name.toLowerCase().includes(searchLower) ||
            doc.playerName?.toLowerCase().includes(searchLower) ||
            doc.description?.toLowerCase().includes(searchLower);

        // Category filter
        const matchesCategory = categoryFilter === 'all' || doc.category === categoryFilter;

        return matchesSearch && matchesCategory;
    });

    // Group documents by player for the summary view
    const playerDocCounts = documents.reduce<Record<string, number>>((acc, doc) => {
        if (doc.playerName) {
            acc[doc.playerName] = (acc[doc.playerName] || 0) + 1;
        }
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8 max-w-6xl">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-display font-bold text-white">Documents & Storage</h1>
                    <p className="text-secondary-400">{documents.length} documents • {Object.keys(playerDocCounts).length} players with files</p>
                </div>
                <button
                    onClick={() => setShowUploadModal(true)}
                    className="btn-primary"
                >
                    + Upload Document
                </button>
            </div>

            {/* Search & Filters */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
                <div className="flex-1 min-w-[200px]">
                    <input
                        type="text"
                        placeholder="Search by name, player, or description..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="input"
                    />
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setCategoryFilter('all')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${categoryFilter === 'all'
                                ? 'bg-primary-500 text-white'
                                : 'bg-secondary-800 text-secondary-300 hover:bg-secondary-700'
                            }`}
                    >
                        All
                    </button>
                    {CATEGORIES.map(cat => (
                        <button
                            key={cat.value}
                            onClick={() => setCategoryFilter(cat.value)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${categoryFilter === cat.value
                                    ? 'bg-primary-500 text-white'
                                    : 'bg-secondary-800 text-secondary-300 hover:bg-secondary-700'
                                }`}
                        >
                            {cat.label.split(' ')[0]}
                        </button>
                    ))}
                </div>
            </div>

            {/* Documents Table */}
            <div className="card overflow-x-auto">
                {filteredDocuments.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-4xl mb-4">📁</p>
                        <p className="text-secondary-400">
                            {documents.length === 0
                                ? 'No documents uploaded yet'
                                : 'No documents match your search'}
                        </p>
                        {documents.length === 0 && (
                            <button
                                onClick={() => setShowUploadModal(true)}
                                className="btn-secondary mt-4"
                            >
                                Upload your first document
                            </button>
                        )}
                    </div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-secondary-700">
                                <th className="text-left py-3 px-3 text-secondary-400 font-medium">Document</th>
                                <th className="text-left py-3 px-3 text-secondary-400 font-medium">Player</th>
                                <th className="text-left py-3 px-3 text-secondary-400 font-medium">Category</th>
                                <th className="text-left py-3 px-3 text-secondary-400 font-medium">Uploaded</th>
                                <th className="text-right py-3 px-3 text-secondary-400 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDocuments.map(doc => {
                                const catInfo = getCategoryInfo(doc.category);
                                return (
                                    <tr key={doc.id} className="border-b border-secondary-800 hover:bg-secondary-800/50">
                                        <td className="py-3 px-3">
                                            <div>
                                                <span className="font-medium text-white">{doc.name}</span>
                                                {doc.description && (
                                                    <p className="text-xs text-secondary-400 truncate max-w-xs">{doc.description}</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-3 px-3">
                                            {doc.playerName ? (
                                                <span className="text-sm text-secondary-300">{doc.playerName}</span>
                                            ) : (
                                                <span className="text-secondary-500">—</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-3">
                                            <span className={`text-xs px-2 py-1 rounded ${catInfo.color}`}>
                                                {catInfo.label}
                                            </span>
                                        </td>
                                        <td className="py-3 px-3 text-secondary-400 text-sm">
                                            {doc.uploadedAt.toLocaleDateString()}
                                        </td>
                                        <td className="py-3 px-3 text-right">
                                            <div className="flex justify-end gap-2">
                                                <a
                                                    href={doc.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="px-2 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 rounded text-blue-400"
                                                >
                                                    View
                                                </a>
                                                <button
                                                    onClick={() => handleDelete(doc)}
                                                    className="px-2 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 rounded text-red-400"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Player Quick Stats */}
            {Object.keys(playerDocCounts).length > 0 && (
                <div className="mt-8">
                    <h2 className="text-lg font-display font-bold text-white mb-4">Documents by Player</h2>
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(playerDocCounts)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([name, count]) => (
                                <button
                                    key={name}
                                    onClick={() => setSearchQuery(name)}
                                    className="px-3 py-1.5 bg-secondary-700 hover:bg-secondary-600 rounded-lg text-sm text-secondary-300 flex items-center gap-2"
                                >
                                    <span>{name}</span>
                                    <span className="bg-primary-500/20 text-primary-400 px-1.5 py-0.5 rounded text-xs">
                                        {count}
                                    </span>
                                </button>
                            ))}
                    </div>
                </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="card w-full max-w-md">
                        <h2 className="text-xl font-bold text-white mb-4">Upload Document</h2>
                        <form onSubmit={handleUpload} className="space-y-4">
                            <div>
                                <label className="label">Document Name *</label>
                                <input
                                    type="text"
                                    className="input"
                                    value={uploadForm.name}
                                    onChange={e => setUploadForm(f => ({ ...f, name: e.target.value }))}
                                    placeholder="e.g., Permission Slip - Kettering"
                                    required
                                />
                            </div>
                            <div>
                                <label className="label">Description</label>
                                <textarea
                                    className="input resize-none"
                                    rows={2}
                                    value={uploadForm.description}
                                    onChange={e => setUploadForm(f => ({ ...f, description: e.target.value }))}
                                    placeholder="Optional notes about this document"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">Category</label>
                                    <select
                                        className="input"
                                        value={uploadForm.category}
                                        onChange={e => setUploadForm(f => ({ ...f, category: e.target.value as any }))}
                                    >
                                        {CATEGORIES.map(cat => (
                                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="label">Associated Player</label>
                                    <select
                                        className="input"
                                        value={uploadForm.playerId}
                                        onChange={e => setUploadForm(f => ({ ...f, playerId: e.target.value }))}
                                    >
                                        <option value="">No player (general doc)</option>
                                        {players.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.lastName}, {p.firstName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="label">File *</label>
                                <input
                                    type="file"
                                    className="input"
                                    onChange={e => setUploadForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                    required
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowUploadModal(false)}
                                    className="btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary flex-1"
                                    disabled={uploading}
                                >
                                    {uploading ? 'Uploading...' : 'Upload'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
